import { User } from '../../modules/user/shared/models/user';
import { AuthenticatedUser } from './authenticated-user';

export class UserStatus {
    static readonly PENDING = 'Pending';
    static readonly ACTIVE = 'Active';
    static readonly INACTIVE = 'Inactive';
    static readonly REJECTED = 'Rejected';
    static readonly INCOMPLETE = 'Incomplete';

    static readonly permittedUserStatusesForUserReview = [
        UserStatus.PENDING,
        UserStatus.REJECTED,
        UserStatus.INCOMPLETE,
    ];

    static getStatuses() {
        return [
            UserStatus.PENDING,
            UserStatus.ACTIVE,
            UserStatus.INACTIVE,
            UserStatus.REJECTED,
            UserStatus.INCOMPLETE,
        ];
    }

    /**
     * Determines the status of a user based on their active, approved, and registered states.
     *
     * @param user - The user object whose status is to be determined.
     * @returns The status of the user as a string.
     */
    static getUserStatus(user: User) {
        return this.getStatus(
            user.isActive,
            user.isApproved,
            user.isRegistered,
        );
    }

    /**
     * Determines the status of an authenticated user based on their active, approved, and registered states.
     *
     * @param user - The authenticated user object whose status is to be determined.
     * @returns The status of the authenticated user as a string.
     */
    static getAuthenticatedUserStatus(user: AuthenticatedUser) {
        return this.getStatus(
            user.isActive,
            user.isApproved,
            user.isRegistered,
        );
    }

    /**
     * Determines the status of a user based on their active, approved, and registered states.
     *
     * @param isActive - Indicates if the user is active.
     * @param isApproved - Indicates if the user is approved. Can be null if the approval is pending.
     * @param isRegistered - Indicates if the user has completed registration.
     * @returns The status of the user as a string.
     */
    private static getStatus(
        isActive: boolean,
        isApproved: boolean | null,
        isRegistered: boolean,
    ) {
        if (isApproved) {
            return isActive ? UserStatus.ACTIVE : UserStatus.INACTIVE;
        }

        if (!isRegistered) {
            return UserStatus.INCOMPLETE;
        }

        if (isApproved == null) {
            return UserStatus.PENDING;
        }

        return UserStatus.REJECTED;
    }
}
